import { useEffect, useMemo, useState } from "react";

import { dashboardPaths } from "@outlets/configs/options";
import { fetchActivityInsights } from "./activityInsightsApi";

// Backoffice › Uso da plataforma: o que o tracking (`utils/activityTracker.js`) gravou em
// `user_activity` — páginas mais acessadas, ações/botões, uso por usuário e por obra, e o
// histórico de eventos. Só leitura.

const TYPE_LABELS = Object.freeze({
  page_view: "Página acessada",
  report_download: "Relatório baixado",
  project_selected: "Obra selecionada",
  login: "Login",
  logout: "Logout",
  password_changed: "Senha alterada",
  data_update_requested: "Atualização de dados pedida",
  file_uploaded: "Arquivo enviado",
});

const STATIC_ROUTE_LABELS = Object.freeze({
  "/": "Raiz",
  "/access": "Login",
  "/access/recuperar-senha": "Recuperar senha",
  "/access/definir-senha": "Definir senha",
  "/access/codigo-verificacao": "Código de verificação",
  "/dashboard": "Dashboard",
  "/dashboard/:id": "Dashboard",
  "/dashboard/settings": "Configurações",
  "/dashboard/sobre/:id": "Sobre",
  "/dashboard/director": "Diretoria",
});

const BACKOFFICE_LABELS = Object.freeze({
  users: "Usuários",
  "companies-buildings": "Construtoras e Obras",
  indexers: "Indexadores",
  "cartesian-forms": "Formulários",
  "supplier-explorer": "Explorador de fornecedores",
  audit: "Auditoria",
  integrations: "Integrações",
  "sienge-permissions": "Permissões Sienge",
  "product-access": "Gestão de Produtos",
  "financial-governance": "Governança Financeira",
  activity: "Uso da plataforma",
});

const DASHBOARD_LABELS = Object.fromEntries(dashboardPaths.map((item) => [item.path, item.label]));

export const routeLabel = (route) => {
  if (!route) return "—";
  if (STATIC_ROUTE_LABELS[route]) return STATIC_ROUTE_LABELS[route];
  const parts = route.split("/").filter(Boolean);
  if (parts[0] !== "dashboard") return route;
  if (DASHBOARD_LABELS[parts[1]]) return DASHBOARD_LABELS[parts[1]];
  if (parts[1] === "backoffice") return `Backoffice › ${BACKOFFICE_LABELS[parts[2]] ?? parts[2]}`;
  if (parts[1] === "director") return `Diretoria › ${parts.slice(2).join(" › ")}`;
  if (parts[1] === "costs") return `Custos › ${parts[2] ?? ""}`;
  if (parts[1] === "bim") return `BIM › ${parts[2] ?? ""}`;
  return route;
};

const actionLabel = (row) => {
  const base = TYPE_LABELS[row.type] ?? row.type;
  const detail = row.report ?? row.detail;
  return detail ? `${base} · ${detail}` : base;
};

const dateInput = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const formatDay = (value) => {
  const [year, month, day] = String(value).split("-");
  return day && month ? `${day}/${month}` : value;
};

const number = (value) => Number(value ?? 0).toLocaleString("pt-BR");

const TABS = Object.freeze([
  { key: "pages", label: "Páginas" },
  { key: "actions", label: "Ações e botões" },
  { key: "users", label: "Usuários" },
  { key: "projects", label: "Obras" },
  { key: "events", label: "Eventos" },
]);

const Stat = ({ label, value }) => (
  <div className="activity-insights__stat">
    <span>{label}</span>
    <strong>{number(value)}</strong>
  </div>
);

const Bar = ({ value, max }) => (
  <span className="activity-insights__bar" aria-hidden="true">
    <span style={{ width: `${max ? Math.max((value / max) * 100, 2) : 0}%` }} />
  </span>
);

const DailyChart = ({ daily }) => {
  if (!daily?.length) return null;
  const max = Math.max(...daily.map((day) => day.events));
  return (
    <section className="activity-insights__panel" aria-label="Eventos por dia">
      <h2>Eventos por dia</h2>
      <div className="activity-insights__daily" role="list">
        {daily.map((day) => (
          <div
            key={day.date}
            role="listitem"
            className="activity-insights__day"
            title={`${formatDay(day.date)}: ${number(day.events)} eventos, ${number(day.users)} usuários`}
          >
            <span className="activity-insights__day-bar" style={{ height: `${max ? Math.max((day.events / max) * 100, 2) : 0}%` }} />
            <span className="activity-insights__day-label">{formatDay(day.date)}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const ActivityInsights = () => {
  const today = useMemo(() => new Date(), []);
  const [filters, setFilters] = useState({
    from: dateInput(new Date(today.getTime() - 29 * 86400000)),
    to: dateInput(today),
    email: "",
    type: "",
    route: "",
    projectId: "",
  });
  const [applied, setApplied] = useState(filters);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("pages");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchActivityInsights({ ...applied, page, pageSize: 50 })
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) setError(err?.message || "Erro ao carregar."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [applied, page]);

  const apply = (next) => {
    setPage(1);
    setApplied(next);
  };

  const submit = (event) => {
    event.preventDefault();
    apply(filters);
  };

  // Clicar numa linha filtra o painel inteiro por ela.
  const drill = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    apply(next);
    setTab("events");
  };

  const clearFilters = () => {
    const next = { ...filters, email: "", type: "", route: "", projectId: "" };
    setFilters(next);
    apply(next);
  };

  const maxRoute = Math.max(0, ...(data?.topRoutes ?? []).map((row) => row.views));
  const maxAction = Math.max(0, ...(data?.topActions ?? []).map((row) => row.count));
  const maxProject = Math.max(0, ...(data?.topProjects ?? []).map((row) => row.events));
  const hasDrillFilter = applied.email || applied.type || applied.route || applied.projectId;

  return (
    <div className="activity-insights">
      <header className="activity-insights__header">
        <p className="activity-insights__eyebrow">Backoffice</p>
        <h1>Uso da plataforma</h1>
        <p className="activity-insights__hint">
          Páginas acessadas e ações registradas pelo tracking (usuários identificados só por e-mail).
        </p>
      </header>

      <form className="activity-insights__filters" onSubmit={submit}>
        <label>
          De
          <input type="date" value={filters.from} max={filters.to} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
        </label>
        <label>
          Até
          <input type="date" value={filters.to} min={filters.from} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        </label>
        <label>
          E-mail
          <input type="search" placeholder="usuario@empresa.com" value={filters.email} onChange={(e) => setFilters({ ...filters, email: e.target.value })} />
        </label>
        <label>
          Tipo de evento
          <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="">Todos</option>
            {Object.entries(TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <div className="activity-insights__filter-actions">
          <button type="submit" className="button button-accent small" disabled={loading}>Aplicar</button>
          {hasDrillFilter ? (
            <button type="button" className="button button-secondary small" onClick={clearFilters}>Limpar filtros</button>
          ) : null}
        </div>
      </form>

      {applied.route || applied.projectId ? (
        <p className="activity-insights__active-filters">
          {applied.route ? <>Página: <strong>{routeLabel(applied.route)}</strong> </> : null}
          {applied.projectId ? <>Obra: <strong>{data?.topProjects?.find((row) => row.projectId === applied.projectId)?.buildingName ?? applied.projectId}</strong></> : null}
        </p>
      ) : null}

      {error ? <p className="activity-insights__error" role="alert">{error}</p> : null}
      {loading && !data ? <p className="activity-insights__muted">Carregando…</p> : null}

      {data ? (
        <div className={loading ? "activity-insights__body is-loading" : "activity-insights__body"}>
          <div className="activity-insights__stats">
            <Stat label="Eventos" value={data.totals.events} />
            <Stat label="Usuários ativos" value={data.totals.users} />
            <Stat label="Páginas vistas" value={data.totals.pageViews} />
            <Stat label="Downloads" value={data.totals.downloads} />
            <Stat label="Obras acessadas" value={data.totals.projects} />
          </div>

          <DailyChart daily={data.daily} />

          <div className="activity-insights__tabs" role="tablist">
            {TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={tab === item.key}
                className={tab === item.key ? "active" : ""}
                onClick={() => setTab(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {tab === "pages" ? (
            <table className="activity-insights__table">
              <thead><tr><th>Página</th><th>Rota</th><th>Acessos</th><th>Usuários</th><th>Último acesso</th></tr></thead>
              <tbody>
                {data.topRoutes.map((row) => (
                  <tr key={row.route} onClick={() => drill({ route: row.route, type: "page_view" })} className="clickable-row">
                    <td>{routeLabel(row.route)}</td>
                    <td className="activity-insights__mono">{row.route}</td>
                    <td><Bar value={row.views} max={maxRoute} />{number(row.views)}</td>
                    <td>{number(row.users)}</td>
                    <td>{formatDateTime(row.lastAt)}</td>
                  </tr>
                ))}
                {data.topRoutes.length === 0 ? <tr><td colSpan={5}>Nenhuma página acessada no período.</td></tr> : null}
              </tbody>
            </table>
          ) : null}

          {tab === "actions" ? (
            <table className="activity-insights__table">
              <thead><tr><th>Ação</th><th>Vezes</th><th>Usuários</th><th>Última vez</th></tr></thead>
              <tbody>
                {data.topActions.map((row) => (
                  <tr key={`${row.type}-${row.report}-${row.detail}`} onClick={() => drill({ type: row.type })} className="clickable-row">
                    <td>{actionLabel(row)}</td>
                    <td><Bar value={row.count} max={maxAction} />{number(row.count)}</td>
                    <td>{number(row.users)}</td>
                    <td>{formatDateTime(row.lastAt)}</td>
                  </tr>
                ))}
                {data.topActions.length === 0 ? <tr><td colSpan={4}>Nenhuma ação registrada no período.</td></tr> : null}
              </tbody>
            </table>
          ) : null}

          {tab === "users" ? (
            <table className="activity-insights__table">
              <thead>
                <tr><th>Usuário</th><th>Eventos</th><th>Páginas vistas</th><th>Páginas distintas</th><th>Downloads</th><th>Dias ativos</th><th>Primeiro acesso</th><th>Último acesso</th></tr>
              </thead>
              <tbody>
                {data.byUser.map((row) => (
                  <tr key={row.email} onClick={() => drill({ email: row.email })} className="clickable-row">
                    <td>{row.email}</td>
                    <td>{number(row.events)}</td>
                    <td>{number(row.pageViews)}</td>
                    <td>{number(row.distinctPages)}</td>
                    <td>{number(row.downloads)}</td>
                    <td>{number(row.activeDays)}</td>
                    <td>{formatDateTime(row.firstAt)}</td>
                    <td>{formatDateTime(row.lastAt)}</td>
                  </tr>
                ))}
                {data.byUser.length === 0 ? <tr><td colSpan={8}>Nenhum usuário ativo no período.</td></tr> : null}
              </tbody>
            </table>
          ) : null}

          {tab === "projects" ? (
            <table className="activity-insights__table">
              <thead><tr><th>Obra</th><th>Eventos</th><th>Usuários</th><th>Último acesso</th></tr></thead>
              <tbody>
                {data.topProjects.map((row) => (
                  <tr key={row.projectId} onClick={() => drill({ projectId: row.projectId })} className="clickable-row">
                    <td>{row.buildingName ?? <span className="activity-insights__mono">{row.projectId}</span>}</td>
                    <td><Bar value={row.events} max={maxProject} />{number(row.events)}</td>
                    <td>{number(row.users)}</td>
                    <td>{formatDateTime(row.lastAt)}</td>
                  </tr>
                ))}
                {data.topProjects.length === 0 ? <tr><td colSpan={4}>Nenhuma obra acessada no período.</td></tr> : null}
              </tbody>
            </table>
          ) : null}

          {tab === "events" ? (
            <>
              <table className="activity-insights__table">
                <thead><tr><th>Quando</th><th>Usuário</th><th>Evento</th><th>Página</th><th>Detalhes</th></tr></thead>
                <tbody>
                  {data.recent.items.map((row) => (
                    <tr key={row._id}>
                      <td>{formatDateTime(row.at)}</td>
                      <td>{row.email}</td>
                      <td>{TYPE_LABELS[row.type] ?? row.type}</td>
                      <td title={row.path ?? ""}>{routeLabel(row.route)}</td>
                      <td className="activity-insights__mono">
                        {Object.entries(row.metadata ?? {}).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`).join(" · ") || "—"}
                      </td>
                    </tr>
                  ))}
                  {data.recent.items.length === 0 ? <tr><td colSpan={5}>Nenhum evento no período.</td></tr> : null}
                </tbody>
              </table>
              {data.recent.totalPages > 1 ? (
                <div className="activity-insights__pagination">
                  <button type="button" className="button button-secondary small" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Anterior</button>
                  <span>Página {page} de {data.recent.totalPages} ({number(data.recent.totalItemsCount)} eventos)</span>
                  <button type="button" className="button button-secondary small" disabled={page >= data.recent.totalPages || loading} onClick={() => setPage(page + 1)}>Próxima</button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ActivityInsights;
